// ─────────────────────────────────────────────────────────────────────────────
// Generic Logo kaynak sayfasi: ust grid (CustomDataGrid) + alt detay form (Splitter).
// Flat entity'ler (Malzeme, Tedarikci, Fiyatlar) bunu bir config ile kullanir.
// Ticket sayfasi duzenini taklit eder; her entity yine kendi route/sayfasidir.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Splitter, { Item as SplitterItem } from 'devextreme-react/splitter';
import Form, { SimpleItem, TabbedItem, TabPanelOptions, Tab, Label } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import notify from 'devextreme/ui/notify';
import { CustomDataGrid } from './CustomDataGrid';
import { LogoResult } from '../types';
import { lt } from '../i18n';

export interface FieldSpec {
  field: string;
  label: string;
  editor?: 'text' | 'number' | 'check' | 'date' | 'select';
  items?: Array<{ id: any; name: string }>;
  colSpan?: number;
  required?: boolean;
  tab?: string;      // ayni tab adi -> ayni sekmede gruplanir
  readOnly?: boolean;
}

export interface ResourceConfig<T> {
  keyExpr: string;
  columns: any[];                                   // CustomDataGrid ColumnConfig[]
  fields?: FieldSpec[];                             // detay form; yoksa sadece goruntu
  load: (onlyActive: boolean) => Promise<LogoResult<T[]>>;
  save?: (item: T) => Promise<LogoResult<any>>;     // yoksa salt-okunur
  newItem?: () => T;
  headerLabel: (item: T) => string;
  hasActiveFilter?: boolean;
  /** Detay formunun altina ek icerik (orn. satir alt-grid'i). */
  extraDetail?: (model: T) => React.ReactNode;
}

type DxEditor = 'dxTextBox' | 'dxNumberBox' | 'dxCheckBox' | 'dxDateBox' | 'dxSelectBox';

function editorType(f: FieldSpec): DxEditor {
  switch (f.editor) {
    case 'number': return 'dxNumberBox';
    case 'check': return 'dxCheckBox';
    case 'date': return 'dxDateBox';
    case 'select': return 'dxSelectBox';
    default: return 'dxTextBox';
  }
}

function editorOptions(f: FieldSpec): any {
  const o: any = { readOnly: f.readOnly };
  if (f.editor === 'select') { o.items = f.items ?? []; o.valueExpr = 'id'; o.displayExpr = 'name'; }
  if (f.editor === 'date') { o.type = 'date'; o.displayFormat = 'dd.MM.yyyy'; }
  return o;
}

// ─── Detay form ──────────────────────────────────────────────────────────────

function ResourceDetail<T extends Record<string, any>>({ config, item, onSaved, onCancel }: {
  config: ResourceConfig<T>;
  item: T | null;
  onSaved: (saved: T) => void;
  onCancel: () => void;
}) {
  const [model, setModel] = useState<T>(() => (item ? { ...item } : (config.newItem?.() as T)));
  const [saving, setSaving] = useState(false);
  const modelRef = useRef<T>(model);

  useEffect(() => {
    const next = item ? { ...item } : (config.newItem?.() as T);
    setModel(next);
    modelRef.current = next;
  }, [item, config]);

  const isNew = !item;
  const canSave = !!config.save;
  const fields = config.fields ?? [];
  const tabs = useMemo(() => {
    const names: string[] = [];
    for (const f of fields) { const t = f.tab ?? lt('general'); if (!names.includes(t)) names.push(t); }
    return names;
  }, [fields]);

  const handleSave = async () => {
    if (!config.save) return;
    setSaving(true);
    const res = await config.save(modelRef.current);
    setSaving(false);
    if (res.ok) {
      notify(lt('savedOk'), 'success', 2500);
      onSaved((res.data as T) ?? modelRef.current);
    } else {
      notify(`${lt('saveError')}: ${res.message ?? ''}`, 'error', 4000);
    }
  };

  const formKey = useMemo(() => config.headerLabel(model) + (isNew ? '-new' : ''), [model, isNew, config]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
        <strong style={{ fontSize: 14 }}>{isNew ? lt('newRecord') : config.headerLabel(model)}</strong>
        <span style={{ flex: 1 }} />
        {canSave && <Button text={lt('save')} type="default" stylingMode="contained" disabled={saving} onClick={handleSave} />}
        <Button text={lt('cancel')} stylingMode="text" onClick={onCancel} />
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
        <Form key={formKey} formData={model} labelLocation="top" showColonAfterLabel colCount={2}>
          <TabbedItem>
            <TabPanelOptions deferRendering={false} />
            {tabs.map((tab) => (
              <Tab key={tab} title={tab} colCount={2}>
                {fields.filter((f) => (f.tab ?? lt('general')) === tab).map((f) => (
                  <SimpleItem
                    key={f.field}
                    dataField={f.field}
                    editorType={editorType(f)}
                    editorOptions={editorOptions(f)}
                    isRequired={f.required}
                    colSpan={f.colSpan}
                  >
                    <Label text={f.label} />
                  </SimpleItem>
                ))}
              </Tab>
            ))}
          </TabbedItem>
        </Form>
        {config.extraDetail?.(model)}
      </div>
    </div>
  );
}

// ─── Sayfa ───────────────────────────────────────────────────────────────────

export function ResourceListPage<T extends Record<string, any>>({ config }: { config: ResourceConfig<T> }) {
  const canEnter = !!config.save && !!config.fields?.length;
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlyActive, setOnlyActive] = useState(true);
  const [selected, setSelected] = useState<T | null>(null);
  // Ticket sayfasi gibi: altta giris bolumu hep hazir (varsayilan yeni-kayit formu).
  const [creating, setCreating] = useState(canEnter);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await config.load(onlyActive);
    setLoading(false);
    if (res.ok) setRows(res.data ?? []);
    else { notify(`${lt('loadError')}: ${res.message ?? ''}`, 'error', 4000); setRows([]); }
  }, [config, onlyActive]);

  useEffect(() => { load(); }, [load]);

  const openRow = useCallback((c: T) => { setCreating(false); setSelected(c); }, []);
  const startNew = useCallback(() => { setSelected(null); setCreating(true); }, []);
  const handleSaved = useCallback((saved: T) => { setCreating(false); setSelected(saved); load(); }, [load]);
  // Kapatinca placeholder yerine bos giris formuna don (giris bolumu hep acik kalsin).
  const closeDetail = useCallback(() => { setSelected(null); setCreating(canEnter); }, [canEnter]);

  const hasForm = !!config.fields?.length;
  const showDetail = hasForm && (creating || !!selected);

  const toolbarItems = useMemo(() => (
    config.hasActiveFilter
      ? [{
          location: 'before' as const,
          render: () => (
            <CheckBox value={onlyActive} text={lt('onlyActive')} onValueChanged={(e: any) => setOnlyActive(!!e.value)} />
          ),
        }]
      : []
  ), [config.hasActiveFilter, onlyActive]);

  return (
    <div style={{ height: 'calc(100vh - 120px)' }}>
      <Splitter orientation="vertical" style={{ height: '100%' }}>
        <SplitterItem size={hasForm ? '60%' : '100%'} minSize="120px" collapsible>
          <CustomDataGrid
            data={rows}
            columns={config.columns}
            keyExpr={config.keyExpr}
            height="100%"
            t={lt}
            selectionEnabled={false}
            filterRowVisible
            noDataText={loading ? '...' : lt('noData')}
            onRowClick={(data: T) => openRow(data)}
            onAddNew={config.save ? startNew : undefined}
            onRefresh={load}
            additionalToolbarItems={toolbarItems}
          />
        </SplitterItem>

        {hasForm && (
          <SplitterItem size="40%" minSize="120px" collapsible>
            {showDetail ? (
              <ResourceDetail
                config={config}
                item={creating ? null : selected}
                onSaved={handleSaved}
                onCancel={closeDetail}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
                {lt('selectRecord')}
              </div>
            )}
          </SplitterItem>
        )}
      </Splitter>
    </div>
  );
}
